import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Grid, Paper, Table, TableBody, TableContainer, TablePagination } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PleaseWait from '../Shared/PleaseWait';
import FileRow from './FileRow';
import FileUploader from './FileUploader';
import Alert from './Alert';
import { inject, observer } from 'mobx-react';
import FileTableHeader from './FileTableHeader';
import ConfirmDialog from '../Shared/ConfirmDialog';

const useStyles = makeStyles(() => ({
  table: {
    border: '1px solid #ddd',
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: '#f5f5f5',
  },
  oddRow: {
    backgroundColor: '#f9f9f9',
  },
  evenRow: {
    backgroundColor: '#ffffff',
  },
  tableRow: {
    height: '20px',
  },
  tableCell: {
    paddingTop: '5px',
    paddingBottom: '5px',
  },
}));

const FileHostingList = inject('FileHostStore')(
  observer(({ FileHostStore, authStore }) => {
    const classes = useStyles();
    const [scormFiles, setScormFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [totalFiles, setTotalFiles] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [openDelete, setOpenDelete] = useState(false);
    const fileToDelete = useRef({});
    const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

    useEffect(() => {
      return () => {
        FileHostStore.deleteStore();
      };
    }, [FileHostStore]);

    const fetchData = useCallback(
      async (authStore, page, perPage, orderBy = '', orderDirection = '', filters = {}) => {
        setLoading(true);
        setError(null);
        setMessage(null);
        try {
          await FileHostStore.retrieve(authStore, page, perPage, orderBy, orderDirection, filters);
          const { scormFiles = [], totalFiles = 0, error } = FileHostStore.state;
          if (scormFiles.length === 0 && page > 1 && totalFiles > 0) {
            setPage((prevPage) => prevPage - 1);
            return;
          }

          setScormFiles(scormFiles);
          setTotalFiles(totalFiles);
          if (error) {
            setError(error);
          }
        } catch (err) {
          setError(err.message || 'An error occurred while fetching files.');
        } finally {
          setLoading(false);
        }
      },
      [FileHostStore],
    );

    useEffect(() => {
      fetchData(authStore, page, perPage).then(() => setLoading(false));
    }, [authStore, page, perPage, fetchData]);

    const handlePageChange = (event, newPage) => {
      setPage(newPage + 1);
    };

    const handleRowsPerPageChange = (event) => {
      setPerPage(parseInt(event.target.value, 10));
      setPage(1);
    };

    const handleDeleteFile = async (authStore, fileId, fileName) => {
      try {
        setLoading(true);
        await FileHostStore.deleteCourse(authStore, fileId);
        await fetchData(authStore, page, perPage);
        setMessage(`'${fileName}' deleted successfully!`);
      } catch (err) {
        setError(err.message || 'Failed to delete the file.');
      } finally {
        setLoading(false);
      }
    };

    const handleUploadComplete = async (fileName) => {
      await fetchData(authStore, page, perPage);
      setMessage(`'${fileName}' uploaded successfully!`);
    };

    const handleOpenDelete = (fileId, fileName) => {
      setOpenDelete(!openDelete);
      fileToDelete.current = { id: fileId, name: fileName };
    };

    const handleClose = (confirm) => {
      setOpenDelete(false);
      if (confirm) {
        handleDeleteFile(authStore, fileToDelete.current.id, fileToDelete.current.name).then(() => setLoading(false));
      }
      fileToDelete.current = {};
    };

    const handleSort = (key) => {
      setLoading(true);
      setSortConfig((prevConfig) => {
        const isSameKey = prevConfig.key === key;
        const newDirection = isSameKey && prevConfig.direction === 'asc' ? 'desc' : 'asc';
        return { key, direction: newDirection };
      });
      setPage(1);
      fetchData(authStore, 1, perPage, key, sortConfig.direction === 'asc' ? 'desc' : 'asc').then(() => setLoading(false));
    };

    const handleFilter = (filters) => {
      setLoading(true);
      fetchData(authStore, 1, perPage, sortConfig.key, sortConfig.direction, filters).then(() => setLoading(false));
    };

    const handleUploadClick = () => {
      setError(null);
      setMessage(null);
    };

    return (
      <>
        <PleaseWait loading={loading} />
        <Grid container direction="row" justifyContent="center" alignItems="center" style={{ marginLeft: '20px' }}>
          <FileUploader onUploadComplete={handleUploadComplete} authStore={authStore} disableUpload={loading}
                        FileHostStore={FileHostStore} clickedUploadButton={handleUploadClick} />
          {!!error && <Alert severity="error" message={error} />}
          {!!message && <Alert severity="info" message={message} />}
          {scormFiles.length > 0 ? (
            <TableContainer component={Paper}>
              <Table className={classes.table}>
                <FileTableHeader sortConfig={sortConfig} handleSort={handleSort} className={classes.headerCell}
                                 handleFilter={handleFilter} />
                <TableBody>
                  {scormFiles.map((file, index) => (
                    <FileRow
                      key={file.id}
                      file={file}
                      onDelete={handleOpenDelete}
                      style={index % 2 === 0 ? classes.evenRow : classes.oddRow}
                      classes={{ tableCell: classes.tableCell }}
                    />
                  ))}
                </TableBody>
              </Table>
              <Box display="flex" justifyContent="flex-end">
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
                  count={totalFiles}
                  rowsPerPage={perPage}
                  page={page - 1}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </Box>
              <ConfirmDialog
                open={openDelete}
                title={`Delete \`${fileToDelete.current?.name}\``}
                description="Deleting this file will permanently remove it. Any existing links to it will no longer function. Are you sure you want to continue?"
                handleClose={() => handleClose(false)}
                handleContinue={() => handleClose(true)}
                cancelLabel="No"
                continueLabel="Yes"
              />
            </TableContainer>
          ) : (
            !loading && <p>No files found</p>
          )}
        </Grid>
      </>
    );
  }),
);

export default FileHostingList;
